import { z } from 'zod'

/**
 * A packet (docs/sales.spec.md, "Sending a packet"): what it is, where it
 * stands, and the groups its lines are in. Mirror the `SalesPacketKind`,
 * `SalesPacketStatus` and `SalesLineGroup` enums in the Prisma schema — they
 * must stay in sync.
 */

/** `booking`: proposal, contract and schedule for an inquiry. `invoice` and `amendment` come after signing. */
export const SALES_PACKET_KINDS = ['booking', 'invoice', 'amendment'] as const
export const salesPacketKindSchema = z.enum(SALES_PACKET_KINDS)
export type SalesPacketKind = z.infer<typeof salesPacketKindSchema>

/** The agreement: the booking and a change to it. An invoice asks for money; it is not the agreement. */
export const AGREEMENT_PACKET_KINDS = ['booking', 'amendment'] as const satisfies readonly SalesPacketKind[]

export function isAgreementPacketKind(kind: SalesPacketKind): kind is (typeof AGREEMENT_PACKET_KINDS)[number] {
  return AGREEMENT_PACKET_KINDS.some((agreement) => agreement === kind)
}

export const SALES_PACKET_STATUSES = ['open', 'signing', 'signed', 'withdrawn', 'expired', 'declined', 'lost'] as const
export const salesPacketStatusSchema = z.enum(SALES_PACKET_STATUSES)
export type SalesPacketStatus = z.infer<typeof salesPacketStatusSchema>

/** Statuses in which a packet holds its date against other packets and bookings. */
export const HOLDING_PACKET_STATUSES = ['open', 'signing', 'signed'] as const satisfies readonly SalesPacketStatus[]

/** Closed without a signature, while the signing envelope may still be open: withdrawn, lapsed, or lost. */
export const VOIDABLE_PACKET_STATUSES = ['withdrawn', 'expired', 'lost'] as const satisfies readonly SalesPacketStatus[]

export const SALES_PACKET_STATUS_LABELS: Readonly<Record<SalesPacketStatus, string>> = {
  open: 'Sent',
  signing: 'Being signed',
  signed: 'Signed',
  withdrawn: 'Withdrawn',
  expired: 'Lapsed',
  declined: 'Declined',
  lost: 'Inquiry lost',
}

/** `package`: the couple picks one. `add_on`: they choose any. `included`: always there. */
export const SALES_LINE_GROUPS = ['package', 'add_on', 'included'] as const
export const salesLineGroupSchema = z.enum(SALES_LINE_GROUPS)
export type SalesLineGroup = z.infer<typeof salesLineGroupSchema>
